import { SortOrder } from 'antd/lib/table/interface'
import axios, { AxiosError, CancelTokenSource } from 'axios'
import isObject from 'lodash/isObject'
import { flow } from 'mobx'

import { DEFAULT_ERROR_MESSAGE, TIMEOUT_ERROR_CODE } from 'Core/constants'
import { CustomGenerator, SortDirection } from 'Core/interfaces'
import notificationService from 'Core/services/NotificationService'

export const requestWrapper = flow(function* <Value, DefaultValue, RequestParams>({
	field,
	repository,
	repositoryParams,
	showDefaultError = true,
	successCallback,
	errorCallback,
	mapResponseCallback
}: {
	field: { value: Value | DefaultValue; isLoading: boolean }
	repository: (params: RequestParams) => Promise<Value>
	repositoryParams: RequestParams
	showDefaultError?: boolean
	successCallback?: () => void
	errorCallback?: (err: AxiosError) => void
	mapResponseCallback?: (value: Value) => Value
}): CustomGenerator<Value, Value> {
	field.isLoading = true
	try {
		let value = yield repository(repositoryParams)

		if (mapResponseCallback) {
			value = mapResponseCallback(value)
		}

		field.value = value
		successCallback?.()
		return value
	} catch (err) {
		const error = err as AxiosError
		if (error?.code === TIMEOUT_ERROR_CODE) {
			notificationService.showErrorNotification(error)
		} else if (showDefaultError) {
			notificationService.showNotification({ type: 'error', msg: DEFAULT_ERROR_MESSAGE })
		}
		errorCallback?.(err as AxiosError)
		throw err
	} finally {
		field.isLoading = false
	}
})

export const createAndCheckToken = (source: CancelTokenSource | null) => {
	source?.cancel('Operation canceled by the user.')
	return axios.CancelToken.source()
}

export type ErrorData<T> = {
	errors?: T
}

export const getErrorMessageByKey = <T extends { [key in keyof T]?: string }>(
	error: AxiosError<ErrorData<T>>
): string | undefined => {
	if (!error.response || !error.response.data || !error.response.data.errors) {
		return
	}

	const { errors }: ErrorData<T> = error.response.data
	const fieldName = Object.keys(errors)[0] as keyof T

	return errors[fieldName]
}

export const parseErrorsToString = (
	errors: Record<string, string[]> | undefined,
	msg: string = DEFAULT_ERROR_MESSAGE
): string => {
	if (isObject(errors)) {
		return Object.values(errors)
			.reduce((previousValue, currentValue) => [...previousValue, ...currentValue], [])
			.join(', ')
	}
	return msg
}

export const formatAntdSortOrderToRequest = (
	antdOrder: SortOrder | undefined,
	defaultOrder: SortDirection
) => {
	switch (antdOrder) {
		case 'ascend':
			return SortDirection.ASC
		case 'descend':
			return SortDirection.DESC
		default:
			return defaultOrder
	}
}
